// Third-party
import React from 'react';
import getUserLocale from 'get-user-locale';
import { createIntl, createIntlCache, IntlShape, useIntl } from 'react-intl';
import countries from 'i18n-iso-countries';
import countriesEn from 'i18n-iso-countries/langs/en.json';
import countriesFr from 'i18n-iso-countries/langs/fr.json';
import moment from 'moment';
import 'moment/dist/locale/fr-ca';
import 'moment/dist/locale/en-ca';

// Utils
import { flatten } from '../objects';
import localStorage from '../local-storage';

// I18n
import messagesEnCA from '../../i18n/en-CA.json';
import messagesEnUS from '../../i18n/en-US.json';
import messagesFrCA from '../../i18n/fr-CA.json';

// Types
import { IntlState } from '../../redux/modules/types/intl';
import Dictionary from '@/types/dictionary';
import Locale from '@/types/locale';
import MessageSet from '@/types/message-set';
import LocalizedString from '@/types/localized-string';

export enum LOCALE {
  EN_CA = 'en-CA',
  EN_US = 'en-US',
  FR_CA = 'fr-CA',
}

export const LOCALE_OPTIONS = [
  { value: LOCALE.EN_CA, label: 'English (Canada)' },
  { value: LOCALE.EN_US, label: 'English (US)' },
  { value: LOCALE.FR_CA, label: 'Français' },
];

export const CONTACTS_LOCALE = [
  { value: LOCALE.EN_CA, label: 'English CAN' },
  { value: LOCALE.EN_US, label: 'English US' },
  { value: LOCALE.FR_CA, label: 'Français' },
];

const LOCALE_FALLBACKS: Record<LOCALE, LOCALE[]> = {
  [LOCALE.EN_CA]: [],
  [LOCALE.FR_CA]: [],
  [LOCALE.EN_US]: [LOCALE.EN_CA],
};

const MESSAGES: Record<Locale, Object> = {
  [LOCALE.EN_CA]: flatten(messagesEnCA),
  [LOCALE.EN_US]: flatten(messagesEnUS),
  [LOCALE.FR_CA]: flatten(messagesFrCA),
};

const DEFAULT_LOCALE = LOCALE.EN_CA;

export const DEFAULT_DECIMALS = 2;

type NumberSuffixesByLocale = {
  MILLION: Record<LOCALE, string>;
  BILLION: Record<LOCALE, string>;
};

const NUMBER_SUFFIXES: NumberSuffixesByLocale = {
  MILLION: {
    [LOCALE.EN_CA]: 'M',
    [LOCALE.EN_US]: 'M',
    [LOCALE.FR_CA]: 'M',
  },
  BILLION: {
    [LOCALE.EN_CA]: 'B',
    [LOCALE.EN_US]: 'B',
    [LOCALE.FR_CA]: 'B',
  },
};

const ONE_MILLION = 1000000;
const ONE_BILLION = 1000000000;

let locale: Locale;
let intl: IntlShape;

export type StartMonth = 'jan' | 'apr' | 'jul' | 'oct';

const intlCache = createIntlCache();

let intlProviders: Record<LOCALE, IntlShape | undefined> = {
  [LOCALE.EN_CA]: undefined,
  [LOCALE.EN_US]: undefined,
  [LOCALE.FR_CA]: undefined,
};

const fyStartMonthSubstract = (startMonth: StartMonth) => {
  switch (startMonth) {
    case 'jan':
      return 0;
    case 'apr':
      return 3;
    case 'jul':
      return 6;
    case 'oct':
      return 9;
    default:
      return 0;
  }
};

/**
 * Returns the locale and messages specific to requested locale.
 * @param locale Locale to be used.
 * @returns Locale data.
 */
export const setCurrentLocale = (newLocale: Locale) => {
  locale = MESSAGES[newLocale] ? newLocale : DEFAULT_LOCALE;

  moment.locale(locale);

  return {
    locale,
    messages: MESSAGES[locale],
  };
};

/**
 * Adds values to the app's currently stored dictionnary of messages
 * @param messages New values to add to the dictionnary
 * @returns The newly updated set of the current locale's messages
 */
export const addStoredMessages = (messages: MessageSet) => {
  for (const [key, value] of Object.entries(messages)) {
    if (MESSAGES[key as Locale] && value) {
      const messageLocale = key as Locale;
      MESSAGES[messageLocale] = { ...MESSAGES[messageLocale], ...flatten(value) };
    }
  }

  intlProviders[LOCALE.FR_CA] = createIntl({ locale: LOCALE.FR_CA, messages: flatten(MESSAGES[LOCALE.FR_CA]) }, intlCache);
  intlProviders[LOCALE.EN_CA] = createIntl({ locale: LOCALE.EN_CA, messages: flatten(MESSAGES[LOCALE.EN_CA]) }, intlCache);
  intlProviders[LOCALE.EN_US] = createIntl({ locale: LOCALE.EN_US, messages: flatten(MESSAGES[LOCALE.EN_US]) }, intlCache);

  return MESSAGES[locale];
};

/**
 * Initialize the react-intl service and load proper locale data
 * @returns Initial state to be used in Redux reducer
 */
export const init = (): IntlState => {
  countries.registerLocale(countriesEn);
  countries.registerLocale(countriesFr);

  intlProviders = {
    [LOCALE.EN_CA]: createIntl({ locale: LOCALE.EN_CA, messages: flatten(MESSAGES[LOCALE.EN_CA]) }, intlCache),
    [LOCALE.EN_US]: createIntl({ locale: LOCALE.EN_US, messages: flatten(MESSAGES[LOCALE.EN_US]) }, intlCache),
    [LOCALE.FR_CA]: createIntl({ locale: LOCALE.FR_CA, messages: flatten(MESSAGES[LOCALE.FR_CA]) }, intlCache),
  };

  let initLocale = localStorage.getLocale();
  if (initLocale == null) {
    const browserLocale = getUserLocale();

    switch (browserLocale) {
      case 'fr-CA':
      case 'fr': {
        initLocale = LOCALE.FR_CA;
        break;
      }
      case 'en-US': {
        initLocale = LOCALE.EN_US;
        break;
      }
      case 'en-CA':
      case 'en':
      default: {
        initLocale = LOCALE.EN_CA;
        break;
      }
    }
  }

  return setCurrentLocale(initLocale);
};

const getIntlProvider = (localeKey: Locale) => {
  return intlProviders[localeKey];
};

/**
 * Translate a message key using currently loaded locale file
 * @param id Id of the message to be translated
 * @param values Map containing data to inject in translated string
 * @param defaultMessage Default message to be used if no translation is defined for the specified id (the id will be used if no message is specified).
 * @returns The translated message
 */
export const formatMessage = (id: string, values?: Dictionary<string | number>, defaultMessage?: string) =>
  intl && intl.formatMessage({ id, defaultMessage }, values);

/**
 * Translate a message key using currently loaded locale file
 * @param id Id of the message to be translated
 * @param toLocale the target locale of the message
 * @param values Map containing data to inject in translated string
 * @param defaultMessage Default message to be used if no translation is defined for the specified id (the id will be used if no message is specified).
 * @returns The translated message
 */
export const formatMessageWithLocale = (id: string, toLocale?: Locale, values?: Dictionary<string | number>, defaultMessage?: string): string => {
  if (toLocale) {
    return getIntlProvider(toLocale)?.formatMessage({ id, defaultMessage }, values) || '';
  }

  return intl && intl.formatMessage({ id, defaultMessage }, values);
};

/**
 * Translate an HTML message key using currently loaded locale file
 * @param id Id of the message to be translated
 * @param toLocale the target locale of the message
 * @param values Map containing data to inject in translated string
 * @param defaultMessage Default message to be used if no translation is defined for the specified id (the id will be used if no message is specified).
 * @returns The translated HTML message
 */
export const formatHTMLMessageWithLocale = (id: string, toLocale?: Locale, values?: Dictionary<string | number>, defaultMessage?: string): string => {
  if (toLocale) {
    return getIntlProvider(toLocale)?.formatMessage({ id, defaultMessage }, values, { ignoreTag: true }) || '';
  }

  return intl && intl.formatMessage({ id, defaultMessage }, values, { ignoreTag: true });
};

/**
 * Format a date to the specified locale and Moment format
 * @param date Date to be formatted
 * @param toLocale Locale
 * @param format Moment format
 * @returns The formatted date
 */
export const formatDateWithLocale = (date: Date | moment.Moment, toLocale: Locale, format: string) =>
  moment(date).locale(toLocale.toLowerCase()).utc().format(format);

const getFinancialDate = (date: Date | moment.Moment, startMonth: StartMonth) =>
  moment(date)
    .startOf('month')
    .subtract(fyStartMonthSubstract(startMonth as StartMonth), 'month');
/**
 * Converts a date to its fiscal quarter
 * @param date Date to be converted
 * @param startMonth The first month of the fiscal year
 * @returns The fiscal quarter
 */
export const getFinancialQuarterFromDate = (date: Date | moment.Moment, startMonth: string) => getFinancialDate(date, startMonth as StartMonth).quarter();

/**
 * Converts a date to its fiscal year
 * @param date Date to be converted
 * @param startMonth The first month of the fiscal year
 * @returns The fiscal year
 */
export const getFinancialYearFromDate = (date: Date | moment.Moment, startMonth: string) => getFinancialDate(date, startMonth as StartMonth).year();

/**
 * Generate a LocalizedString object from translation string
 * @param id Id of the message
 * @returns The LocalizedString
 */
export const generateLocalizedString = (id: string, values?: Dictionary<string | number>): LocalizedString =>
  Object.values(LOCALE).reduce(
    (prev, curr) => ({
      ...prev,
      [curr]: formatMessageWithLocale(id, curr, values),
    }),
    {},
  );

/**
 * Generate a LocalizedString object and assign text to all locales
 * @param text String to be assigned to all locales
 * @returns The LocalizedString
 */
export const generateLocalizedStringFromText = (text: string): LocalizedString =>
  Object.values(LOCALE).reduce(
    (prev, curr) => ({
      ...prev,
      [curr]: text,
    }),
    {},
  );

/**
 * Format ordinal suffixes based on locale and leverages our translation files
 * English has more rules for one, two, few and other while French only has rules for
 * one and other.
 * @param number
 */
export const formatOrdinalSuffix = (number: number, locale = localStorage.getLocale()) => {
  const ordinalSuffixesMap = new Map([
    ['one', 'Common.OrdinalSuffix.One'],
    ['two', 'Common.OrdinalSuffix.Two'],
    ['few', 'Common.OrdinalSuffix.Few'],
    ['other', 'Common.OrdinalSuffix.Other'],
  ]);
  if (!locale) {
    return undefined;
  }
  const pluralRules = new Intl.PluralRules(locale, { type: 'ordinal' });
  const rule = pluralRules.select(number);
  const suffixId = ordinalSuffixesMap.get(rule);
  return suffixId && formatMessageWithLocale(suffixId, locale);
};

/**
 * Formats a number using the currently loaded locale
 * @param {number} number number to format
 * @param {number} numberFractionDigits Number of digits after the period (default: 2)
 * @returns The formatted and localized number
 */
export const formatNumber = (number: number, decimals: number = DEFAULT_DECIMALS, toLocale: Locale = locale) =>
  Number(number).toLocaleString(toLocale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

/**
 * Formats a percentage using the currently loaded locale
 * @param {number} number Number to format
 * @param {number} numberFractionDigits Number of digits after the period (default: 2)
 * @returns The formatted and localized number in percentage
 */
export const formatPercentage = (number: number, decimals: number = DEFAULT_DECIMALS, toLocale: Locale = locale) => {
  const numberAsPercentage = number / 100.0;

  return Number(numberAsPercentage).toLocaleString(toLocale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Formats a number to currency value
 *
 * @param {number} number Number of dollars to format
 * @param {number} decimals Decimal precision of currency value (default: 2)
 * @param {string} currency Currency prefix (ex. CAD)
 * @param {boolean} withSuffixes Replace million and billion dollar values with 'M' or 'D'. Defaults to `true`
 *@param {Locale} toLocale Target Locale
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (
  number: number,
  decimals: number = DEFAULT_DECIMALS,
  currency?: string,
  withSuffixes: boolean = true,
  toLocale: Locale = locale,
): string => {
  // Format numbers over a million with suffixes (ie: CAD 1.4M)
  // Currently handled in a custom fashion - Intl.NumberFormat doesn't seem to
  // achieve what we're looking for.
  if (withSuffixes && number >= ONE_MILLION) {
    let value;
    let suffix;

    if (number < ONE_BILLION) {
      suffix = NUMBER_SUFFIXES.MILLION[toLocale];
      value = formatNumber(number / ONE_MILLION, decimals, toLocale);
    } else {
      suffix = NUMBER_SUFFIXES.BILLION[toLocale];
      value = formatNumber(number / ONE_BILLION, decimals, toLocale);
    }

    switch (toLocale) {
      case LOCALE.FR_CA: {
        return `${value} ${suffix}${currency ? ` ${currency}` : ''}`;
      }
      default: {
        return `${currency ? `${currency} ` : ''}${value}${suffix}`;
      }
    }
  }

  const isNegative = number < 0;
  const absNumber = Math.abs(number);

  const str = absNumber.toLocaleString(toLocale, {
    style: 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  switch (toLocale) {
    case LOCALE.FR_CA: {
      return isNegative ? `-${str} $` : `${str} $`;
    }
    default: {
      return isNegative ? `-$${str}` : `$${str}`;
    }
  }
};

/**
 * Formats a localized string based on loaded locale
 * @param {LocalizedString} stringToFormat string to format
 * @returns The localized string
 */
export const formatLocalizedString = (stringToFormat?: LocalizedString, toLocale?: Locale): string => {
  if (!stringToFormat) {
    return '';
  }

  // Prioritize set locale if any - else look for the user's locale. If neither exist, use default locale.
  const targetLocale = toLocale || locale;
  const localesToCheck = [targetLocale, ...(LOCALE_FALLBACKS[targetLocale] ?? []), DEFAULT_LOCALE];

  for (const localeToCheck of localesToCheck) {
    const localeStr = stringToFormat[localeToCheck];
    if (localeStr) {
      return localeStr;
    }
  }

  return '';
};

export const getCountryName = (countryCode: string, toLocale: Locale = locale) => {
  if (!countryCode) {
    return formatMessageWithLocale('Common.NotApplicable', toLocale);
  }
  if (countryCode === 'Unidentified') {
    return formatMessageWithLocale('Comparison.Table.CountryAllocations.Unidentified', toLocale);
  }

  const lang = getLang(toLocale);

  return countries.getName(countryCode, lang) || countryCode;
};

export const getLang = (toLocale: Locale) => {
  let lang: string;
  switch (toLocale) {
    case LOCALE.FR_CA: {
      lang = 'fr';
      break;
    }
    case LOCALE.EN_CA:
    case LOCALE.EN_US:
    default: {
      lang = 'en';
      break;
    }
  }

  return lang;
};

export const formatBytes = (bytes: number, toLocale: Locale = locale): string => {
  const units = [
    formatMessageWithLocale('Common.FileSizeUnits.B.Abbr', toLocale),
    formatMessageWithLocale('Common.FileSizeUnits.KB.Abbr', toLocale),
    formatMessageWithLocale('Common.FileSizeUnits.MB.Abbr', toLocale),
    formatMessageWithLocale('Common.FileSizeUnits.GB.Abbr', toLocale),
    formatMessageWithLocale('Common.FileSizeUnits.TB.Abbr', toLocale),
    formatMessageWithLocale('Common.FileSizeUnits.PB.Abbr', toLocale),
  ];

  const trimZeroesRegex = new RegExp(`\\${formatMessageWithLocale('Common.DecimalSeparator', toLocale)}?0+$`);
  const unitIndex = Math.floor(Math.log10(bytes) / 3);
  const formattedValue = formatNumber(bytes / 1000 ** unitIndex, 2, toLocale).replace(trimZeroesRegex, '');

  return `${formattedValue}\xa0${units[unitIndex]}`;
};

/**
 * Formats a list of strings with commas and a conjonction in appropriate language before the last element.
 * In example, ['a', 'b', 'c'] would return 'a, b <conjonction> c' in English. Supported conjonctions are 'and' and 'or'.
 * @param {string[]} list List of strings to format
 * @param {('and'|'or')} conjunction Conjonction to use before the last element.
 * @param {Locale} toLocale Target Locale
 * @returns {string} localised string
 */
export const formatIterationWithConjunction = (list: string[], conjunction: 'and' | 'or', toLocale: Locale = locale): string => {
  if (list.length === 0) {
    return '';
  }
  let conjonctionKey;
  switch (conjunction) {
    case 'or':
      conjonctionKey = 'Common.Or';
      break;
    case 'and':
      conjonctionKey = 'Common.And';
      break;
    // To add more cases, add more keys in the Common namespace and add them to the switch statement.
  }
  const localizedConjonction = formatMessageWithLocale(conjonctionKey, toLocale);
  // If the list has more than one element, join all elements with commas and add an 'and' before the last element.
  // Else, return the only element in the list.
  return list.length > 1 ? `${list.slice(0, -1).join(', ')} ${localizedConjonction} ${list.slice(-1)}` : list[0];
};

/**
 * This util component is used to intercept the IntlProvider, which will be used
 * in the exported formatMessage function
 */
const IntlGlobalProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  intl = useIntl();
  return <>{children}</>;
};

export default IntlGlobalProvider;
